import React, { useState, useEffect } from "react";
import { login } from "../store/session";
import { useDispatch } from "react-redux";
import { useModal } from "../context/Modal";
import { useNavigate, useLocation } from "react-router-dom";
import { FiUser, FiEye, FiEyeOff, FiCheck, FiLock } from "react-icons/fi";
import SignupForm from "./SignupForm";

function LoginForm({ onError, closeMenu }) {
  const dispatch = useDispatch();
  const { closeModal, setModalContent } = useModal();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    if (state?.openModal) {
      setModalContent(<LoginForm />);
    }
  }, [state, setModalContent]);

  // Scoped override: neutralize #modal-content's white card while this glass
  // form is mounted so the backdrop-blur actually shows something behind it.
  // Restored on unmount so every other modal keeps its original look.
  useEffect(() => {
    const el = document.getElementById("modal-content");
    if (!el) return undefined;
    const prev = {
      background: el.style.background,
      boxShadow: el.style.boxShadow,
      borderRadius: el.style.borderRadius,
      maxWidth: el.style.maxWidth,
      overflow: el.style.overflow,
    };
    el.style.background = "transparent";
    el.style.boxShadow = "none";
    el.style.borderRadius = "0";
    el.style.maxWidth = "460px";
    el.style.overflow = "visible";
    // Soften the pitch-black overlay so the frosted glass tint reads.
    const bg = document.getElementById("modal-background");
    let prevBg = null;
    if (bg) {
      prevBg = bg.style.backgroundColor;
      bg.style.backgroundColor = "rgba(15, 23, 42, 0.55)";
    }
    return () => {
      Object.assign(el.style, prev);
      if (bg) bg.style.backgroundColor = prevBg;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const errorMessages = await dispatch(login(email, password, rememberMe));

    if (errorMessages) {
      setErrors(errorMessages);
      if (onError) onError(errorMessages);
    } else {
      setErrors([]);
      closeModal();
    }
    setLoading(false);
  };

  const handleForgotPasswordClick = () => {
    closeModal();
    if (closeMenu) closeMenu();
    navigate("/forgot-password");
  };

  const openSignup = () => setModalContent(<SignupForm />);

  // Glassmorphism reusable styles.
  const glassPanel = {
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.10) 60%, rgba(255,255,255,0.20) 100%)",
    backdropFilter: "blur(24px) saturate(180%)",
    WebkitBackdropFilter: "blur(24px) saturate(180%)",
    border: "1px solid rgba(255,255,255,0.45)",
    boxShadow:
      "0 8px 32px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.55)",
    borderRadius: "1.75rem",
  };
  const glassField =
    "w-full h-12 pl-12 pr-12 rounded-full bg-white/10 border border-white/40 text-white placeholder:text-white/70 focus:outline-none focus:border-white/80 focus:bg-white/15 transition-colors";

  return (
    <div
      className="p-8 sm:p-10 text-white"
      style={glassPanel}
    >
      <h2 className="text-4xl font-bold mb-2 tracking-tight drop-shadow">Login</h2>
      <p className="text-white/85 mb-8 text-sm">
        Welcome back — please login to your account
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {errors.length > 0 && (
          <ul
            className="rounded-2xl px-4 py-2 text-sm"
            style={{
              background: "rgba(220, 38, 38, 0.25)",
              border: "1px solid rgba(254, 202, 202, 0.65)",
              color: "#fff",
              backdropFilter: "blur(6px)",
            }}
          >
            {errors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        )}

        {/* User / email field */}
        <div className="relative">
          <FiUser
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
            size={18}
            aria-hidden="true"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={glassField}
            placeholder="User Name"
            aria-label="Email"
          />
        </div>

        {/* Password field */}
        <div className="relative">
          <FiLock
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
            size={18}
            aria-hidden="true"
          />
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={glassField}
            placeholder="Password"
            aria-label="Password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
          </button>
        </div>

        {/* Remember Me + Forgot */}
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="peer sr-only"
            />
            <span
              className="w-6 h-6 rounded-md flex items-center justify-center border border-white/60 bg-white/15 peer-checked:bg-white peer-checked:border-white transition-all"
              aria-hidden="true"
            >
              {rememberMe && <FiCheck className="text-emerald-700" size={16} />}
            </span>
            <span className="text-white text-sm">Remember me</span>
          </label>
          <button
            type="button"
            className="text-white/85 hover:text-white text-sm underline-offset-2 hover:underline"
            onClick={handleForgotPasswordClick}
          >
            Forgot Password?
          </button>
        </div>

        {/* Primary CTA */}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-14 rounded-full text-white text-lg font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 100%)",
            border: "1px solid rgba(255,255,255,0.6)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 20px rgba(0,0,0,0.25)",
            backdropFilter: "blur(6px)",
          }}
        >
          {loading ? "Logging In…" : "Login"}
        </button>

        {/* Signup link */}
        <p className="text-center text-white/90 text-sm pt-1">
          Don&apos;t have an account?{" "}
          <button
            type="button"
            onClick={openSignup}
            className="font-semibold underline underline-offset-2 hover:text-white"
          >
            Signup
          </button>
        </p>

        {/* Social login placeholders (kept from original, still commented) */}
        <div className="flex justify-center mt-2">
          {/* <button
            className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full mr-2 hover:bg-gray-200 transition"
            aria-label="Log in with Google"
          >
            <i className="fab fa-google"></i>
          </button>
          <button
            className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-200 transition"
            aria-label="Log in with Facebook"
          >
            <i className="fab fa-facebook"></i>
          </button> */}
        </div>
      </form>
    </div>
  );
}

export default LoginForm;
